/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2023. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { defineStore } from 'pinia';
import axios from 'axios';
import { DnaApiService } from '@/services/api';
import DnaModel from '@/model/dna/dna.model';
import useNodesStore from '@/store/modules/pinia/nodes';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import { downloadFileInBrowser } from '@/helpers/file-download';

const useDnaStore = defineStore('dna', {
  state: () => ({
    targetConfiguration: new DnaModel({}),
    nodeTargetConfiguration: new DnaModel({}),
    // Distinguishes "reset while a request is pending" from a real n/a response,
    // so the UI can fall back to the node's own status only while loading
    isTargetConfigurationLoading: false,
    isNodeTargetConfigurationLoading: false,
  }),

  actions: {
    async _fetchConfiguration(serialNumber, apiMethod, cancelTokenSetter, stateProperty, loadingProperty) {
      this[loadingProperty] = true;
      try {
        const nodesStore = useNodesStore();
        const source = axios.CancelToken.source();
        nodesStore[cancelTokenSetter](source);

        const response = await apiMethod({ serialNumber, token: source.token });
        // Ignore stale responses if the user has already switched to another node
        if (useNodesStore().selectedNode?.serialNumber !== serialNumber) {
          return;
        }
        this[stateProperty] = new DnaModel(response);
      } catch {
        if (useNodesStore().selectedNode?.serialNumber === serialNumber) {
          this[stateProperty] = new DnaModel({});
        }
      } finally {
        if (useNodesStore().selectedNode?.serialNumber === serialNumber) {
          this[loadingProperty] = false;
        }
      }
    },

    async fetchTargetConfiguration(serialNumber) {
      await this._fetchConfiguration(
        serialNumber,
        (params) => DnaApiService.getWorkloadTargetConfiguration(params),
        'setCancelTokenDnaStatus',
        'targetConfiguration',
        'isTargetConfigurationLoading',
      );
    },
    async fetchTargetConfigurationEmpty() {
      this.targetConfiguration = new DnaModel({});
      this.isTargetConfigurationLoading = true;
    },
    async fetchNodeTargetConfigurationEmpty() {
      this.nodeTargetConfiguration = new DnaModel({});
      this.isNodeTargetConfigurationLoading = true;
    },
    async fetchNodeTargetConfiguration(serialNumber) {
      await this._fetchConfiguration(
        serialNumber,
        (params) => DnaApiService.getNodeTargetConfiguration(params),
        'setCancelTokenNodeDnaStatus',
        'nodeTargetConfiguration',
        'isNodeTargetConfigurationLoading',
      );
    },
    async downloadCurrentConfiguration(serialNumber, typeOfDna) {
      const { data, filename } =
        typeOfDna === 'node'
          ? await DnaApiService.downloadNodeCurrentConfiguration(serialNumber)
          : await DnaApiService.downloadWorkloadCurrentConfiguration(serialNumber);
      downloadFileInBrowser(data, filename);
    },

    async downloadTargetConfiguration(serialNumber, typeOfDna) {
      const { data, filename } =
        typeOfDna === 'node'
          ? await DnaApiService.downloadNodeTargetConfiguration(serialNumber)
          : await DnaApiService.downloadWorkloadTargetConfiguration(serialNumber);
      downloadFileInBrowser(data, filename);
    },

    _notifyIfApplySkipped(response) {
      if (response?.applySkipped) {
        useUtilsStore().showCustomToast({
          text: 'nodes.dna.configurationSame',
          color: 'primary',
          showClose: true,
        });
      }
    },

    async reApplyTargetConfiguration(payload, typeOfDna) {
      if (typeOfDna === 'node') {
        const response = await DnaApiService.reApplyNodeTargetConfiguration(payload);
        this._notifyIfApplySkipped(response);
        return;
      }
      await DnaApiService.reApplyWorkloadTargetConfiguration(payload);
    },

    async applyConfiguration(payload, typeOfDna) {
      if (typeOfDna === 'node') {
        const response = await DnaApiService.applyNodeConfiguration(payload);
        this._notifyIfApplySkipped(response);
        return;
      }
      await DnaApiService.applyWorkloadConfiguration(payload);
    },

    async cancelApplyConfiguration(serialNumber) {
      await DnaApiService.cancelApplyWorkloadConfiguration(serialNumber);
    },

    mqttDna(payload) {
      const { type, ...dnaConfig } = payload;
      const stateProperty = type === 'node' ? 'nodeTargetConfiguration' : 'targetConfiguration';
      // Intermediate mqtt updates (e.g. status changes during reconfiguration) may not carry
      // the "updated" timestamp, keep the last known value instead of flashing n/a.
      const previousUpdated = this[stateProperty]?.updated;
      const newConfiguration = new DnaModel(dnaConfig);
      if (dnaConfig.updated === undefined && previousUpdated) {
        newConfiguration.updated = previousUpdated;
      }
      this[stateProperty] = newConfiguration;
    },
  },
});

export default useDnaStore;
